import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { AuthService } from './services/auth/auth.service';
import { User } from './models/data/user.model';
import { Subscription } from 'rxjs';
import { Theme } from './services/dark-theme/dark-theme.service';

import { DropdownService } from './services/dropdown/dropdown.service';
import { Router, NavigationEnd } from '@angular/router';
import { PageService } from './services/page/page.service';
import { DataFunctions } from './tools/data-functions';
import { SidebarComponent } from './components/content/shared/sidebar/sidebar.component';
import { NavbarComponent } from './components/content/shared/navbar/navbar.component';
import { HomeComponent } from './components/content/home/page/home.component';
import { DarkThemeService } from './services/dark-theme/dark-theme.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [CommonModule, RouterOutlet, NavbarComponent, SidebarComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  userSub: Subscription = {} as Subscription;
  preSurveySub: Subscription = {} as Subscription;
  isAuthenticated: boolean = false;
  preSurveyState = false;
  sideOpen = false;
  surveyPage = false;
  surveyUrlMatches: string[] = [
    'fellowships',
    'sharing',
    'workers',
    'training',
    'children',
    'compassion',
    'current-ethnic-groups',
    'future-ethnic-groups',
    'activities',
    'example-stories',
    'community-reach',
    'additional-stories',
    'discipleship',
    'new-fellowships',
    'outreach-persecuted',
    'outreach-refugees',
    'outreach-children',
    'outreach-ethnic-groups',
    'meeting-needs',
    'additional-outreach',
    'cam-help',
    'obstacles',
    'other-agencies',
    'partnerships',
    'prayer-requests',
    'photo-upload',
    'submit-survey',
  ];
  currentTheme: Theme = 'system';

  constructor(
    private zone: NgZone,
    private authService: AuthService,
    private dropService: DropdownService,
    private pageService: PageService,
    private router: Router,
    private darkThemeService: DarkThemeService,
  ) {
    //media queries with code
    matchMedia('(max-width: 768px)').addListener((mql) => {
      if (mql.matches) {
        this.zone.run(() => {
          if (this.isAuthenticated == true) {
            this.dropService.closeSidebar();
          } else {
            return;
          }
        });
      }
    });
    matchMedia('(min-width: 768px)').addListener((mql) => {
      if (mql.matches) {
        this.zone.run(() => {
          if (this.isAuthenticated == true) {
            this.dropService.openSidebar();
          }
        });
      }
    });

    //get the website status entries from the database and set them in a behavior subject on the page service
    if (this.isAuthenticated) {
      this.pageService.fetchWebsiteStatuses();
    }
  }

  ngOnInit() {
    //sets screen to top after navigating to route
    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      console.log('🌐 APP COMPONENT DEBUG: Navigation ended to:', evt.url);
      console.log('🌐 APP COMPONENT DEBUG: Router config routes:', this.router.config.map(r => r.path || r.redirectTo));
      
      // Ensure navbar doesn't show on login page
      // Only set isAuthenticated to false if user is actually not authenticated
      // This prevents false negatives during skipLocationChange navigation refreshes
      if (evt.url === '/' || evt.url === '') {
        // Check if user is actually authenticated before setting to false
        // This prevents issues when navigating to / with skipLocationChange for refresh
        const currentUser = this.authService.user.value;
        if (!currentUser || !currentUser.username) {
          console.log('🌐 APP COMPONENT DEBUG: On login page, ensuring isAuthenticated is false');
          this.isAuthenticated = false;
        } else {
          console.log('🌐 APP COMPONENT DEBUG: On login page but user is authenticated (likely skipLocationChange refresh), preserving authentication state');
        }
      }
      
      window.scrollTo(0, 0);

      if (evt.url) {
        if (
          DataFunctions.stringIncludesAnyArray(evt.url, this.surveyUrlMatches)
        ) {
          console.log('🌐 APP COMPONENT DEBUG: URL matches survey pattern, setting surveyPage to true');
          // Only set sidebar to closed if we're on mobile, keep desktop behavior
          if (window.innerWidth < 768) {
            this.dropService.open.next(false);
          } else {
            // Ensure sidebar is open on desktop for survey pages
            if (this.isAuthenticated && !this.preSurveyState) {
              this.dropService.openSidebar();
            }
          }
          this.dropService.surveyPage.next(true);
        } else {
          console.log('🌐 APP COMPONENT DEBUG: URL does not match survey pattern, setting surveyPage to false');
          this.dropService.surveyPage.next(false);
        }
      }
    });

    //handles if sidebar is open or closed
    this.dropService.open.subscribe((res) => {
      this.sideOpen = res;
    });

    //subscribe to boolean that determines if we are on a survey page or not
    this.dropService.surveyPage.subscribe((res) => {
      this.surveyPage = res;
    });

    //allows login with refresh
    this.authService.autoLogin();

    //authenticates user and presurvey state is set
    this.userSub = this.authService.user.subscribe((user: User) => {
      if (!user.username) {
        this.isAuthenticated = false;
      } else {
        this.isAuthenticated = true;

        // Set sidebar state based on screen size after authentication
        if (window.innerWidth >= 768) {
          this.dropService.openSidebar();
        }
        // Mobile keeps the default closed state

        this.preSurveySub = this.authService.isPreSurveyState.subscribe(
          (res) => {
            if (res == true) {
              this.preSurveyState = true;
            } else {
              this.preSurveyState = false;
            }
          },
        );
      }
    });

    // Apply the theme when the app initializes
    this.darkThemeService.applyTheme();
  }

  onThemeChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.currentTheme = selectElement.value as Theme;
    this.darkThemeService.setTheme(this.currentTheme);
  }

  //no memory leaks
  ngOnDestroy() {
    this.userSub.unsubscribe();
    this.preSurveySub.unsubscribe();
  }

  showSurveySidebar(show: boolean): void {
    this.dropService.open.next(!show);
  }
}
